import { useState, useEffect } from 'react';
import { FormControl, Select, MenuItem, Box, TextField, IconButton } from '@mui/material';
import { useMiddlewareClient } from '@qwickforge/react-client';
import { Text } from '@qwickapps/react-framework';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

interface Session {
  id: string;
  title: string;
  projectId: string;
  updatedAt?: string;
}

interface SessionSelectorProps {
  projectId: string | undefined;
  selectedSession: Session | null;
  onSessionChange: (session: Session) => void;
  onError?: (error: string) => void;
}

export default function SessionSelector({
  projectId,
  selectedSession,
  onSessionChange,
  onError
}: SessionSelectorProps) {
  const client = useMiddlewareClient();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');

  useEffect(() => {
    if (projectId && client) {
      loadSessions();
    }
  }, [projectId, client]);

  // Reload sessions when selectedSession changes to ensure it's in the list
  useEffect(() => {
    if (selectedSession && client && projectId) {
      // Check if the selected session is in the current list
      const sessionExists = sessions.find(s => s.id === selectedSession.id);
      if (!sessionExists) {
        // Refresh the sessions list to include the new session
        loadSessions();
      }
    }
  }, [selectedSession?.id]);

  const loadSessions = async () => {
    if (!projectId || !client) return;

    try {
      setLoading(true);
      const allSessions: any = await client.sessions.list(undefined, 50, 0);

      // Handle both array and paginated response formats
      const sessionList = Array.isArray(allSessions) ? allSessions : (allSessions.data || allSessions.sessions || []);

      // Filter by projectId and sort by most recent
      const projectSessions = sessionList
        .filter((s: any) => s.projectId === projectId)
        .sort((a: any, b: any) =>
          new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime()
        );

      setSessions(projectSessions);
    } catch (error) {
      console.error('Failed to load sessions:', error);
      if (onError) {
        onError(error instanceof Error ? error.message : 'Failed to load sessions');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSessionChange = (event: any) => {
    const sessionId = event.target.value;
    const session = sessions.find(s => s.id === sessionId);
    if (session) {
      onSessionChange(session);
    }
  };

  const handleStartEditTitle = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent dropdown from opening
    if (!selectedSession) return;
    setEditedTitle(selectedSession.title);
    setIsEditingTitle(true);
  };

  const handleSaveTitle = async () => {
    if (!selectedSession || !client || !editedTitle.trim()) return;

    try {
      await client.sessions.update(selectedSession.id, { title: editedTitle.trim() });

      // Update the selected session
      const updatedSession = { ...selectedSession, title: editedTitle.trim() };
      onSessionChange(updatedSession);

      // Update the sessions list
      setSessions(prev => prev.map(s =>
        s.id === selectedSession.id ? updatedSession : s
      ));

      setIsEditingTitle(false);
    } catch (error) {
      console.error('Failed to update session title:', error);
      if (onError) {
        onError(error instanceof Error ? error.message : 'Failed to update session title');
      }
    }
  };

  const handleCancelEdit = () => {
    setIsEditingTitle(false);
    setEditedTitle('');
  };

  if (!projectId || sessions.length === 0) {
    return selectedSession ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Text
          variant="body2"
          customColor="var(--theme-text-secondary)"
          content={`#${selectedSession.id.substring(0, 8)}: ${selectedSession.title}`}
        />
      </Box>
    ) : null;
  }

  // Editing mode
  if (isEditingTitle) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 250, backgroundColor: 'var(--theme-surface)', px: 1, py: 0.5, borderRadius: 1, border: '1px solid var(--theme-border-light)' }}>
        <TextField
          size="small"
          value={editedTitle}
          onChange={(e) => setEditedTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSaveTitle();
            if (e.key === 'Escape') handleCancelEdit();
          }}
          autoFocus
          sx={{ flex: 1, backgroundColor: 'var(--theme-primary-light)', color: 'var(--theme-text)' }}
        />
        <IconButton size="small" onClick={handleSaveTitle} sx={{backgroundColor: 'var(--theme-secondary)', ":hover": {backgroundColor: 'var(--theme-secondary-light)'}}}>
          <CheckIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={handleCancelEdit} sx={{backgroundColor: 'var(--theme-accent)', ":hover": {backgroundColor: 'var(--theme-accent-light)'}}}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  // Selector mode
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 250, backgroundColor: 'var(--theme-surface)', px: 1, py: 0.5, borderRadius: 1, border: '1px solid var(--theme-border-light)' }}>
      <FormControl size="small" fullWidth>
        <Select
          value={selectedSession?.id || ''}
          onChange={handleSessionChange}
          disabled={loading}
          displayEmpty
          sx={{
            backgroundColor: 'var(--theme-border-light)',
            color: 'var(--theme-text)',
          }}
          renderValue={(selected) => {
            if (!selected) return <Text variant="body2" content="Select Session" />;
            // Use selectedSession prop directly if available, otherwise search in sessions list
            const session = selectedSession?.id === selected
              ? selectedSession
              : sessions.find(s => s.id === selected);
            return session ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Text variant="body2" content={`#${session.id.substring(0, 8)}: ${session.title}`} />
              </Box>
            ) : (
              <Text variant="body2" content="Select Session" />
            );
          }}
        >
          {sessions.map((session) => (
            <MenuItem key={session.id} value={session.id}>
              <Text variant="body2" content={`#${session.id.substring(0, 8)}: ${session.title}`} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {selectedSession && (
        <IconButton size="small" onClick={handleStartEditTitle} sx={{backgroundColor: 'var(--theme-secondary)', ":hover": {backgroundColor: 'var(--theme-secondary-light)'}}}>
          <EditIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}
